const STEPS = [
  {
    num: '01',
    icon: 'ri-file-text-line',
    title: 'Read the incident',
    desc: 'Review a realistic production log snippet and study the signals: timestamps, services, retries, errors, and clues that matter.',
  },
  {
    num: '02',
    icon: 'ri-git-branch-line',
    title: 'Make your call',
    desc: 'Choose the most likely failure classification, decide how severe it is, and select the best immediate next action.',
  },
  {
    num: '03',
    icon: 'ri-scales-3-line',
    title: 'Compare your judgement',
    desc: 'See how your diagnosis compares with ExplainError, earn your score, and build sharper incident instincts over time.',
  },
];

export default function HowItWorks() {
  return (
    <section className="py-24 border-t border-border-subtle">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14">
          <div className="inline-block text-xs font-mono uppercase tracking-widest text-accent mb-3">
            How it works
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary">
            Three decisions. One realistic incident.
          </h2>
          <p className="text-text-secondary mt-3 max-w-2xl mx-auto text-sm leading-relaxed">
            ExplainError Daily is designed to train the judgement behind incident response, not
            just test your memory.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {STEPS.map((step, idx) => (
            <div key={step.num} className="relative group">
              {idx < STEPS.length - 1 && (
                <div className="hidden md:block absolute top-8 left-1/2 w-full h-px border-t border-dashed border-border-subtle" />
              )}
              <div className="bg-bg-panel border border-border-subtle rounded-xl p-6 hover:border-accent/30 transition-all duration-300 relative z-10">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg border border-accent/30 bg-accent/5 text-accent">
                    <i className={`${step.icon} text-xl`} />
                  </div>
                  <span className="font-mono text-4xl font-bold text-border-muted group-hover:text-border-active transition-colors">
                    {step.num}
                  </span>
                </div>
                <h3 className="font-semibold text-text-primary text-lg mb-2">{step.title}</h3>
                <p className="text-text-secondary text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-bg-panel border border-accent/20 rounded-2xl p-6 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-accent mb-3">
                Why this matters
              </div>
              <h3 className="text-2xl font-bold text-text-primary mb-4">
                Better incident response starts with better pattern recognition
              </h3>
              <p className="text-text-secondary text-sm leading-relaxed mb-4">
                Most engineers do not struggle because they lack data. They struggle because the
                first signal is noisy, the wrong hypothesis takes hold, or the next step is unclear.
              </p>
              <p className="text-text-secondary text-sm leading-relaxed">
                This game helps build the instinct to read the signal faster, classify failure more
                accurately, and choose a stronger first move under pressure.
              </p>
            </div>

            <div className="space-y-3">
              {[
                'Train incident judgement',
                'Build debugging instinct',
                'Practise realistic triage thinking',
                'Create a daily habit around production awareness',
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 rounded-lg border border-border-subtle bg-bg-elevated px-4 py-3"
                >
                  <span className="w-2 h-2 rounded-full bg-accent" />
                  <span className="text-sm text-text-secondary">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}